/**
 * ArcGIS Pro–style Dynamic Range Adjustment (DRA) + background-value mask.
 *
 * DRA recalculates stretch statistics from the current display extent instead
 * of the full dataset. Background-value masking treats a fill value (often 0
 * on DEM / DTM / DSM pads) as transparent so black borders disappear without
 * rewriting the file.
 *
 * TiTiler mapping:
 *   - DRA → POST `/cog/statistics` with a viewport GeoJSON polygon
 *   - Background → `nodata=<value>` on the tile template (same channel as the
 *     existing no-data mask; background wins when both are enabled)
 */

import type { ViewerCamera } from '@geosoft/viewer-sdk';
import type { RasterToolCardState } from '@/state/useViewerStore';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { getCanvasMapHostHandle } from '@/engines/openlayers/hostHandle';

export type RasterStatisticsSource = 'dataset' | 'dra';

/**
 * What the masked border becomes. ArcGIS calls the default "No colour"; picking
 * a colour paints the collar and the no-data region instead of hiding them.
 */
export type RasterBackgroundColorMode = 'transparent' | 'color';

/** Lon/lat bbox `[west, south, east, north]` in EPSG:4326. */
export type LonLatBbox = readonly [number, number, number, number];

/**
 * Tile-URL markers for the client-side background key.
 *
 * `nodata=` only masks pixels that are *exactly* the background value. Border
 * pixels survive reprojection and overview averaging as near-background values
 * (3, 7, 11 … around a 0 fill), which is the residual fringe operators see.
 * These markers carry the key level + tolerance to the tile post-processor.
 * TiTiler ignores query params it does not declare, so they ride along safely.
 */
export const GV_BACKGROUND_MASK_PARAM = 'gv_bg_mask';
export const GV_BACKGROUND_LEVEL_PARAM = 'gv_bg_level';
export const GV_BACKGROUND_TOLERANCE_PARAM = 'gv_bg_tol';
/** Border colour as `rrggbb`; absent means "No colour" (transparent). */
export const GV_BACKGROUND_FILL_PARAM = 'gv_bg_fill';

/** Default fringe tolerance (0–255) applied when background masking is on. */
export const DEFAULT_BACKGROUND_TOLERANCE = 8;

/** Starting swatch for the border colour, used once the mode leaves transparent. */
export const DEFAULT_BACKGROUND_COLOR = '#ffffff';

const DEFAULT_VIEWPORT_ASPECT = 16 / 9;
/** Rough screen width used when estimating extent from camera zoom alone. */
const DEFAULT_VIEWPORT_WIDTH_PX = 960;

/**
 * How many steps the viewport is divided into when snapping a DRA extent.
 *
 * 32 means a pan shorter than ~3% of the screen reuses the previous request.
 * Statistics over extents that close are indistinguishable in the stretch, and
 * without the snap every inertial-scroll settle produced its own POST.
 */
const DRA_BBOX_SNAP_STEPS = 32;

/**
 * Effective display nodata for TiTiler tiles.
 *
 * Background value (ArcGIS Mask → "Display background value") takes priority
 * over the COG's metadata nodata when both masks are active — that is the
 * common DEM pad case (min=0 black border) while metadata may be −9999 / −32767.
 */
export function resolveDisplayNodata(
  state: Pick<
    RasterToolCardState,
    'noDataMask' | 'displayBackgroundValue' | 'backgroundValue'
  >,
  profileNodata: number | null | undefined,
): number | null {
  if (
    state.displayBackgroundValue
    && typeof state.backgroundValue === 'number'
    && Number.isFinite(state.backgroundValue)
  ) {
    return state.backgroundValue;
  }
  if (
    state.noDataMask
    && typeof profileNodata === 'number'
    && Number.isFinite(profileNodata)
  ) {
    return profileNodata;
  }
  return null;
}

/** True when either ArcGIS-style mask should write / clear the `nodata` param. */
export function shouldApplyDisplayNodata(
  state: Pick<RasterToolCardState, 'noDataMask' | 'displayBackgroundValue'>,
): boolean {
  return state.displayBackgroundValue || state.noDataMask;
}

/**
 * Estimate a lon/lat viewport bbox from the store camera (center + zoom).
 * Used when a live map handle is unavailable (tests, Giro3D-only paths).
 */
export function viewportBboxFromCamera(
  camera: Pick<ViewerCamera, 'lonLat' | 'zoom'> | null | undefined,
  options?: {
    readonly aspect?: number;
    readonly widthPx?: number;
  },
): LonLatBbox | null {
  if (!camera?.lonLat) return null;
  const [lon, lat] = camera.lonLat;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const zoom = typeof camera.zoom === 'number' && Number.isFinite(camera.zoom)
    ? camera.zoom
    : 10;
  const aspect = options?.aspect ?? DEFAULT_VIEWPORT_ASPECT;
  const widthPx = options?.widthPx ?? DEFAULT_VIEWPORT_WIDTH_PX;
  const heightPx = widthPx / Math.max(0.25, aspect);

  // Web-Mercator metres-per-pixel at equator, scaled by latitude.
  const metersPerPixel =
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
  const halfWidthM = (metersPerPixel * widthPx) / 2;
  const halfHeightM = (metersPerPixel * heightPx) / 2;
  const metersPerDegLat = 111_320;
  const metersPerDegLon = Math.max(
    1e-6,
    111_320 * Math.cos((lat * Math.PI) / 180),
  );
  const dLon = halfWidthM / metersPerDegLon;
  const dLat = halfHeightM / metersPerDegLat;
  const west = clampLon(lon - dLon);
  const east = clampLon(lon + dLon);
  const south = clampLat(lat - dLat);
  const north = clampLat(lat + dLat);
  if (east <= west || north <= south) return null;
  return [west, south, east, north];
}

function clampLon(v: number): number {
  return Math.max(-180, Math.min(180, v));
}

function clampLat(v: number): number {
  return Math.max(-85.05112878, Math.min(85.05112878, v));
}

/** Closed ring polygon GeoJSON for TiTiler POST `/cog/statistics`. */
export function bboxToStatisticsGeoJson(bbox: LonLatBbox): {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  properties: Record<string, never>;
} {
  const [west, south, east, north] = bbox;
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [west, south],
        [east, south],
        [east, north],
        [west, north],
        [west, south],
      ]],
    },
    properties: {},
  };
}

/** Round bbox keys so tiny camera jitter does not thrash DRA fetches. */
export function bboxCacheKey(bbox: LonLatBbox, digits = 4): string {
  return bbox.map((n) => n.toFixed(digits)).join(',');
}

/**
 * Round a raw step down to the nearest power of two.
 *
 * The grid has to be anchored to something other than the extent it came from:
 * two viewports of the "same" size differ in their last float bits, and a step
 * derived straight from the span inherits that, so the snapped corners came out
 * different and the dedupe key missed. Powers of two divide exactly in binary
 * floating point, so any two spans within a factor of two land on one grid.
 */
function gridStep(raw: number): number {
  if (!(raw > 0) || !Number.isFinite(raw)) return 0;
  return 2 ** Math.floor(Math.log2(raw));
}

/**
 * Snap a viewport extent onto a grid proportional to its own span, so a small
 * pan or zoom nudge resolves to the extent already requested.
 *
 * The grid has to scale with the viewport: a fixed decimal precision would be
 * far too coarse at city zoom and far too fine at country zoom, which is what
 * made DRA fire a request per pan step. Centre and span are snapped separately
 * rather than each corner, so a pan moves the extent through one grid cell at a
 * time and holding zoom always reproduces the same span.
 *
 * Callers use the snapped extent for both the dedupe key and the request body,
 * so the two can never disagree.
 */
export function quantizeBbox(
  bbox: LonLatBbox,
  steps = DRA_BBOX_SNAP_STEPS,
): LonLatBbox {
  const [west, south, east, north] = bbox;
  const divisions = Math.max(1, steps);
  const stepX = gridStep((east - west) / divisions);
  const stepY = gridStep((north - south) / divisions);
  if (!(stepX > 0) || !(stepY > 0)) return bbox;

  const snap = (v: number, step: number) => Math.round(v / step) * step;
  // Step is a power of two and the span an integer multiple of it, so halving
  // the span below stays exact.
  const spanX = Math.max(stepX, snap(east - west, stepX));
  const spanY = Math.max(stepY, snap(north - south, stepY));
  const centerX = snap((west + east) / 2, stepX);
  const centerY = snap((south + north) / 2, stepY);

  const snapped: LonLatBbox = [
    clampLon(centerX - spanX / 2),
    clampLat(centerY - spanY / 2),
    clampLon(centerX + spanX / 2),
    clampLat(centerY + spanY / 2),
  ];
  const [w, s, e, n] = snapped;
  return e > w && n > s ? snapped : bbox;
}

/**
 * Real on-screen size of the map host, for the camera-only extent estimate.
 *
 * Without this the estimate assumed a 960×540 window, so DRA computed its
 * stretch over an extent that did not match what the operator was looking at —
 * the same scene could come back with a different stretch on a different
 * window size.
 */
export function viewportSizeOptions(
  container: HTMLElement | null | undefined,
): { readonly widthPx: number; readonly aspect: number } | null {
  const width = container?.clientWidth ?? 0;
  const height = container?.clientHeight ?? 0;
  if (!(width > 0) || !(height > 0)) return null;
  return { widthPx: width, aspect: width / height };
}

/**
 * Extent DRA should compute statistics over: the live map viewport when a host
 * is mounted, else an estimate from the store camera, snapped either way.
 */
export function resolveDraBbox(
  container: HTMLElement | null | undefined,
  camera: Pick<ViewerCamera, 'lonLat' | 'zoom'> | null | undefined,
): LonLatBbox | null {
  const bbox =
    readLiveViewportBbox(container)
    ?? viewportBboxFromCamera(camera, viewportSizeOptions(container) ?? undefined);
  return bbox ? quantizeBbox(bbox) : null;
}

/**
 * Read MapLibre / OpenLayers viewport as EPSG:4326 bbox when a host is mounted.
 * Returns null when neither handle exposes a usable extent.
 */
export function readLiveViewportBbox(container: HTMLElement | null | undefined): LonLatBbox | null {
  if (!container) return null;

  try {
    const maplibre = getMapLibreHostHandle(container);
    if (maplibre) {
      const b = maplibre.getBounds();
      const west = b.getWest();
      const south = b.getSouth();
      const east = b.getEast();
      const north = b.getNorth();
      if (
        Number.isFinite(west)
        && Number.isFinite(south)
        && Number.isFinite(east)
        && Number.isFinite(north)
        && east > west
        && north > south
      ) {
        return [west, south, east, north];
      }
    }
  } catch {
    /* map may be mid-teardown */
  }

  try {
    const canvas = getCanvasMapHostHandle(container);
    if (typeof canvas?.getViewportLonLatBbox === 'function') {
      return canvas.getViewportLonLatBbox() ?? null;
    }
  } catch {
    return null;
  }

  return null;
}
